import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import type { AdminPendingAuction } from '@/types/domain';

const adminKeys = {
  complaints: ['admin', 'complaints'] as const,
  pending: ['admin', 'pending-auctions'] as const,
  bans: ['admin', 'bans'] as const,
};

export function useComplaints() {
  return useQuery({ queryKey: adminKeys.complaints, queryFn: adminApi.listComplaints });
}

export function usePendingAuctions() {
  return useQuery({ queryKey: adminKeys.pending, queryFn: adminApi.listPendingAuctions });
}

export function useBans() {
  return useQuery({ queryKey: adminKeys.bans, queryFn: adminApi.listBans });
}

export function useResolveComplaint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, note }: { id: string; decision: 'ban' | 'dismiss'; note: string | null }) =>
      adminApi.resolveComplaint(id, decision, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.complaints });
      qc.invalidateQueries({ queryKey: adminKeys.bans });
    },
  });
}

export function useModerateAuction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: 'approve' | 'reject'; note: string | null }) =>
      adminApi.moderateAuction(id, action, note),
    onSuccess: (_data, variables) => {
      qc.setQueryData<AdminPendingAuction[] | undefined>(
        adminKeys.pending,
        (prev) => prev?.filter((item) => item.id !== variables.id) ?? prev,
      );
      qc.invalidateQueries({ queryKey: adminKeys.pending });
    },
  });
}

export function useUnban() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, note }: { userId: string; note: string | null }) =>
      adminApi.unbanUser(userId, note),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.bans }),
  });
}

export function useBanByUsername() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      telegramUsername,
      details,
    }: {
      telegramUsername: string;
      details: string | null;
    }) => adminApi.banUserByUsername(telegramUsername, details),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.bans }),
  });
}